import { useCallback, useEffect, useState } from "react";
import { ArrowDown, CreditCard, Gamepad2, Megaphone, Palette, X } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import useAdminAutoRefresh from "../../lib/useAdminAutoRefresh";
import { formatRequestAmount, StatusPill } from "./AdminTransactions";
import { VerifiedName } from "../../components/VerifiedBadge";

const STAT_CARDS = [
  { key: "users", label: "Members", format: "number" },
  { key: "games_on", label: "Games on", format: "number" },
  { key: "pending_reviews", label: "Pending reviews", format: "number" },
  { key: "total_deposits", label: "Total deposits", format: "money" },
  { key: "total_withdrawals", label: "Total withdrawals", format: "money" },
  { key: "member_balance", label: "Member balance", format: "money", accent: true },
];

const SHORTCUTS = [
  { href: "/admin/games", label: "Manage games", icon: Gamepad2 },
  { href: "/admin/payment-methods", label: "Payment methods", icon: CreditCard },
  { href: "/admin/communications", label: "Notice & jackpot", icon: Megaphone },
  { href: "/admin/branding", label: "Branding", icon: Palette },
];

function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

function formatMoney(value) {
  return `$${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function toMemberBalanceRows(rows) {
  return (Array.isArray(rows) ? [...rows] : []).sort((a, b) =>
    Number(b.wallet_balance || 0) - Number(a.wallet_balance || 0)
    || Number(b.free_play_balance || 0) - Number(a.free_play_balance || 0)
    || String(a.username || "").localeCompare(String(b.username || ""), "en", { sensitivity: "base" })
    || String(a.id || a.email || "").localeCompare(String(b.id || b.email || ""), "en")
  );
}

function formatStat(card, stats) {
  const value = stats?.[card.key];
  return card.format === "money" ? formatMoney(value) : formatNumber(value);
}

function formatActivityTime(value) {
  if (!value) return "--:--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--:--";
  return date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [pendingOpen, setPendingOpen] = useState(false);
  const [pendingRows, setPendingRows] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingError, setPendingError] = useState("");
  const [memberBalanceOpen, setMemberBalanceOpen] = useState(false);
  const [memberBalanceRows, setMemberBalanceRows] = useState([]);
  const [memberBalanceSummary, setMemberBalanceSummary] = useState({ count: 0, total: 0, wallet_total: 0, free_play_total: 0 });
  const [memberBalanceLoading, setMemberBalanceLoading] = useState(false);
  const [memberBalanceError, setMemberBalanceError] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/stats");
    setStats(data);
  }, []);

  const loadPendingReviews = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setPendingLoading(true);
    setPendingError("");
    try {
      const { data } = await api.get("/admin/pending-reviews");
      const nextRows = toPendingRows(data);
      setPendingRows(nextRows.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)));
    } catch (err) {
      setPendingError(formatApiError(err));
    } finally {
      if (!quiet) setPendingLoading(false);
    }
  }, []);

  const openPendingReviews = () => {
    setPendingOpen(true);
    loadPendingReviews().catch(() => {});
  };

  const loadMemberBalances = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setMemberBalanceLoading(true);
    setMemberBalanceError("");
    try {
      const { data } = await api.get("/admin/member-balances");
      setMemberBalanceRows(toMemberBalanceRows(data?.members));
      setMemberBalanceSummary({
        count: Number(data?.count || 0),
        total: Number(data?.total || 0),
        wallet_total: Number(data?.wallet_total || 0),
        free_play_total: Number(data?.free_play_total || 0),
      });
    } catch (err) {
      setMemberBalanceError(formatApiError(err));
    } finally {
      if (!quiet) setMemberBalanceLoading(false);
    }
  }, []);

  const openMemberBalances = () => {
    setMemberBalanceOpen(true);
    loadMemberBalances().catch(() => {});
  };

  useEffect(() => {
    load().catch(() => setStats({}));
  }, [load]);

  useAdminAutoRefresh(() => load());
  useAdminAutoRefresh(() => (pendingOpen ? loadPendingReviews({ quiet: true }) : undefined));
  useAdminAutoRefresh(() => (memberBalanceOpen ? loadMemberBalances({ quiet: true }) : undefined));

  const activity = stats?.recent_activity || [];

  return (
    <div className="min-h-full bg-[#08090d] px-2.5 py-3 text-white sm:px-5 sm:py-5 md:px-7">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
        {STAT_CARDS.map((card) => {
          const isPendingCard = card.key === "pending_reviews";
          const isMemberBalanceCard = card.key === "member_balance";
          const isClickableCard = isPendingCard || isMemberBalanceCard;
          const className = `rounded-lg border border-[#3a310f] bg-[#10100f] px-3 py-3 text-left shadow-[0_0_0_1px_rgba(0,0,0,0.3)] sm:px-4 sm:py-4 ${
            isClickableCard ? "transition hover:border-[#2F6FA5] hover:bg-[#15130d] focus:outline-none focus:ring-2 focus:ring-[#2F6FA5]/60" : ""
          }`;
          const content = (
            <>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{card.label}</div>
              <div className={`mt-2 break-words font-mono text-2xl font-black leading-none tracking-normal min-[360px]:text-[28px] sm:text-[32px] ${card.accent ? "text-[#F5C400]" : "text-white"}`}>
                {stats ? formatStat(card, stats) : "-"}
              </div>
              {isPendingCard && <div className="mt-2 text-[11px] font-semibold text-[#2F6FA5]">Click to view pending</div>}
              {isMemberBalanceCard && <div className="mt-2 text-[11px] font-semibold text-[#F5C400]">Click to view members</div>}
            </>
          );
          return isClickableCard ? (
            <button
              key={card.key}
              type="button"
              onClick={isPendingCard ? openPendingReviews : openMemberBalances}
              data-testid={`stat-${card.key}`}
              className={className}
            >
              {content}
            </button>
          ) : (
            <div key={card.key} data-testid={`stat-${card.key}`} className={className}>
              {content}
            </div>
          );
        })}
      </div>

      <div className="mt-6 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {SHORTCUTS.map(({ href, label, icon: Icon }) => (
          <a
            key={href}
            href={href}
            className="inline-flex h-10 min-w-0 items-center justify-center gap-2 rounded-full border border-white/10 bg-[#090a0f] px-3 text-sm font-bold text-white transition hover:border-[#2F6FA5]/60 hover:bg-[#121214] sm:gap-3 sm:px-4"
          >
            <Icon className="h-4 w-4 text-white" />
            <span className="truncate">{label}</span>
          </a>
        ))}
      </div>

      <section className="mt-6 rounded-lg border border-[#3a310f] bg-[#10100f] p-4 md:p-5">
        <h2 className="text-sm font-bold text-white">Recent activity</h2>
        <div className="mt-4 space-y-2 font-mono text-xs">
          {activity.map((item) => (
            <div key={item.id} className="flex min-w-0 items-start gap-2 text-slate-300 sm:gap-3">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400" />
              <span className="shrink-0 text-slate-500">{formatActivityTime(item.time)}</span>
              <span className="shrink-0 text-slate-500">{item.source || "system"}</span>
              <span className="min-w-0">
                <span className="block truncate font-bold text-white">{item.event || "activity"}</span>
                {item.detail ? (
                  <span className="block truncate font-sans text-[11px] font-semibold text-slate-500">
                    {item.detail}
                  </span>
                ) : null}
              </span>
            </div>
          ))}
          {activity.length === 0 && (
            <div className="py-8 text-center font-sans text-sm text-slate-500">
              No recent activity yet.
            </div>
          )}
        </div>
      </section>

      {pendingOpen && (
        <PendingReviewsModal
          error={pendingError}
          loading={pendingLoading}
          rows={pendingRows}
          onClose={() => setPendingOpen(false)}
          onRefresh={() => loadPendingReviews()}
        />
      )}

      {memberBalanceOpen && (
        <MemberBalancesModal
          error={memberBalanceError}
          loading={memberBalanceLoading}
          rows={memberBalanceRows}
          summary={memberBalanceSummary}
          onClose={() => setMemberBalanceOpen(false)}
          onRefresh={() => loadMemberBalances()}
        />
      )}
    </div>
  );
}

function PendingReviewsModal({ error, loading, rows, onClose, onRefresh }) {
  const payments = rows.filter((row) => row.kind === "Payment").length;
  const payouts = rows.filter((row) => row.kind === "Payout").length;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-3 backdrop-blur-sm sm:p-6" onClick={onClose}>
      <div className="max-h-[86vh] w-full max-w-5xl overflow-hidden rounded-2xl border border-[#3a310f] bg-[#10100f] shadow-2xl" onClick={(event) => event.stopPropagation()}>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-4 sm:px-5">
          <div>
            <h2 className="text-xl font-black text-white">Pending reviews</h2>
            <p className="mt-1 text-sm text-slate-500">
              {payments} payments, {payouts} payouts pending
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <a href="/admin/payments-review" className="rounded-full border border-emerald-500/30 px-3 py-2 text-xs font-bold text-emerald-300 hover:bg-emerald-500/10">Payments</a>
            <a href="/admin/payouts-review" className="rounded-full border border-amber-500/30 px-3 py-2 text-xs font-bold text-amber-300 hover:bg-amber-500/10">Payouts</a>
            <button type="button" onClick={onRefresh} className="rounded-full border border-white/10 px-3 py-2 text-xs font-bold text-slate-200 hover:bg-white/5">
              Refresh
            </button>
            <button type="button" onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full border border-white/10 text-slate-300 hover:bg-white/5" aria-label="Close pending reviews">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="max-h-[66vh] overflow-auto">
          {error && <div className="m-4 rounded-xl border border-red-800/50 bg-red-950/30 p-3 text-sm text-red-200">{error}</div>}
          {loading ? (
            <div className="grid min-h-[260px] place-items-center text-sm text-slate-500">Loading pending reviews...</div>
          ) : rows.length ? (
            <table className="w-full min-w-[860px] text-sm">
              <thead className="sticky top-0 bg-[#181817] text-left text-xs uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Member</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                  <th className="px-4 py-3">Method</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3">Note</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-t border-white/5">
                    <td className="px-4 py-3">
                      <span className={`rounded-md border px-2 py-1 text-xs font-bold ${row.kind === "Payout" ? "border-amber-500/30 bg-amber-500/10 text-amber-300" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"}`}>
                        {row.kind}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-bold text-white"><VerifiedName name={row.username || "-"} user={row} /></div>
                      <div className="text-xs text-slate-500">{row.email || "-"}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-bold text-[#F5C400]">{formatRequestAmount(row)}</td>
                    <td className="px-4 py-3 text-slate-300">{pendingMethod(row)}</td>
                    <td className="px-4 py-3"><StatusPill status={row.status} /></td>
                    <td className="px-4 py-3 text-xs text-slate-500">{formatDashboardDate(row.created_at)}</td>
                    <td className="max-w-[260px] truncate px-4 py-3 text-xs text-slate-500">{row.note || row.admin_note || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="grid min-h-[260px] place-items-center text-sm text-slate-500">No pending reviews right now.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function MemberBalancesModal({ error, loading, rows, summary, onClose, onRefresh }) {
  return (
    <div data-testid="member-balances-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-3 backdrop-blur-sm sm:p-6" onClick={onClose}>
      <div className="max-h-[86vh] w-full max-w-5xl overflow-hidden rounded-2xl border border-[#3a310f] bg-[#10100f] shadow-2xl" onClick={(event) => event.stopPropagation()}>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-4 sm:px-5">
          <div>
            <h2 className="text-xl font-black text-white">Member balances</h2>
            <p className="mt-1 text-sm text-slate-500">
              {formatNumber(summary?.count)} members with balance
            </p>
            <p className="mt-1 text-xs font-semibold text-slate-600">
              Wallet total {formatMoney(summary?.wallet_total)} | Freeplay total {formatMoney(summary?.free_play_total)}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <a href="/admin/users" className="rounded-full border border-[#2F6FA5]/40 px-3 py-2 text-xs font-bold text-[#8fc6f5] hover:bg-[#2F6FA5]/10">Open members</a>
            <button type="button" onClick={onRefresh} className="rounded-full border border-white/10 px-3 py-2 text-xs font-bold text-slate-200 hover:bg-white/5">
              Refresh
            </button>
            <button type="button" onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full border border-white/10 text-slate-300 hover:bg-white/5" aria-label="Close member balances">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="max-h-[66vh] overflow-auto">
          {error && <div className="m-4 rounded-xl border border-red-800/50 bg-red-950/30 p-3 text-sm text-red-200">{error}</div>}
          {loading ? (
            <div className="grid min-h-[260px] place-items-center text-sm text-slate-500">Loading member balances...</div>
          ) : rows.length ? (
            <table className="w-full min-w-[820px] text-sm">
              <thead className="sticky top-0 bg-[#181817] text-left text-xs uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="px-4 py-3">Member</th>
                  <th className="px-4 py-3 text-right" aria-sort="descending">
                    <span className="inline-flex items-center justify-end gap-1" title="Highest wallet balance first">
                      Wallet balance <ArrowDown aria-hidden="true" className="h-3 w-3 shrink-0" />
                    </span>
                  </th>
                  <th className="px-4 py-3 text-right">Freeplay</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Joined</th>
                  <th className="px-4 py-3">Updated</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id || row.email || row.username} data-member-id={row.id} className="border-t border-white/5">
                    <td className="px-4 py-3">
                      <div className="font-bold text-white"><VerifiedName name={row.username || "Member"} user={row} /></div>
                      <div className="text-xs text-slate-500">{row.email || "-"}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-bold text-[#F5C400]">{formatMoney(row.wallet_balance)}</td>
                    <td className="px-4 py-3 text-right font-mono text-slate-200">{formatMoney(row.free_play_balance)}</td>
                    <td className="px-4 py-3 text-slate-300">{row.phone || "-"}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{formatDashboardDate(row.created_at)}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{formatDashboardDate(row.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="grid min-h-[260px] place-items-center text-sm text-slate-500">No members currently have wallet or freeplay balance.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function toPendingRows(rows) {
  return (Array.isArray(rows) ? rows : []).map((row) => ({
    ...row,
    kind: row.type === "redeem" ? "Payout" : "Payment",
  }));
}

function pendingMethod(row) {
  return row.payment_platform || row.payment_provider || row.payment_method || row.method || row.game_id || "-";
}

function formatDashboardDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
}
